import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/auth/AuthContext";
import { settingsApi } from "@/lib/settings-api";
import { browserTimeZone } from "@/lib/datetime";
import { todayIso } from "@/lib/date-range";

export const TENANT_TIMEZONE_QUERY_KEY = ["settings", "timezone"] as const;

/**
 * The tenant's configured timezone (`Settings → Timezone`), used to render every
 * tenant-facing date and time. The backend already computes "today" from this
 * value; rendering from it too keeps the two ends in agreement regardless of
 * where the admin's laptop thinks it is.
 *
 * Falls back to the **browser** timezone until settings resolve — already the
 * right answer for staff on local machines, so there's no visible flip for them.
 * Settings change rarely, so the value is cached for the session.
 */
export function useTenantTimezone(): string {
  const { user } = useAuth();

  const { data } = useQuery({
    queryKey: TENANT_TIMEZONE_QUERY_KEY,
    queryFn: async () => (await settingsApi.get()).timezone,
    // Super admins have no tenant settings of their own — the platform console
    // spans tenants, so it stays on the viewer's own timezone.
    enabled: !!user && !!user.tenantId,
    staleTime: 30 * 60_000,
    retry: false,
  });

  return data || browserTimeZone();
}

/**
 * Today's calendar date (YYYY-MM-DD) in the tenant timezone — the anchor for
 * the date-range presets, so "Today" means the tenant's today.
 */
export function useTodayIso(): string {
  return todayIso(useTenantTimezone());
}
